import { Helmet } from 'react-helmet';

import { useStatusToast } from '~/components/unified/hooks';

import { APIKeys } from './APIKeys';
import { APISettings } from './APISettings';
import { APISuppressionSettings } from './APISuppressionSettings';
import { APIVersions } from './APIVersions';
import { APISettingsData, SuppressableAPIType } from './types';
import { useAccountSettings } from './useAccountSettings';

export const APIPage = () => {
  const {
    loading,
    refetch,
    publishableToken,
    secretToken,
    webhookUrl,
    remoteAllowedReferrers,
    apiVersions,
    suppressionSettings,
    updateAccountSettings,
    updateAPIVersion,
    updateSuppressionSettings,
  } = useAccountSettings();

  const { addSuccessToast, addErrorToast } = useStatusToast();

  const handleUpdateSettings = async ({
    webhookUrl,
    remoteAllowedReferrers,
  }: APISettingsData) => {
    const result = await updateAccountSettings({
      variables: {
        input: {
          webhookUrl,
          allowedReferrers: remoteAllowedReferrers,
        },
      },
    });

    if (result.data?.updateAccountSettings?.errors?.length) {
      addErrorToast({
        description:
          'We were unable to update your settings, please try again.',
        heading: 'Settings update failed',
      });
    } else {
      addSuccessToast({
        description: 'Your settings have been updated.',
        heading: 'API settings updated',
      });
    }
  };

  const handleUpdateSuppressionSettings = async (
    apiName: SuppressableAPIType,
  ) => {
    const result = await updateSuppressionSettings({
      variables: {
        input: {
          apiName,
          enabled: !suppressionSettings![apiName],
        },
      },
    });

    const suppressionDisplay =
      apiName === 'prospector' ? 'Prospector' : 'Enrichment';
    const suppressionValueDisplay = !suppressionSettings![apiName]
      ? 'active'
      : 'inactive';

    if (result.data?.updateAccountSuppressionSettings?.errors?.length) {
      addErrorToast({
        description:
          'We were unable to update your suppression settings, please try again.',
        heading: 'Suppression settings update failed',
      });
    } else {
      addSuccessToast({
        description: `${suppressionDisplay} suppression settings have been updated to ${suppressionValueDisplay}`,
        heading: 'Suppression settings updated',
      });
    }

    // This isn't exactly elegant, it's the most straightforward way to update the UI right
    // now since we're not keeping any transient state. But, since it's not exactly the most
    // important toggle in the world, it's fine for now.
    //
    // Something something, state machine (in the future).
    await refetch();
  };

  const handleUpdateAPIVersion = async (value: string) => {
    const result = await updateAPIVersion({
      variables: {
        input: {
          apiName: value,
        },
      },
    });

    if (result.data?.updateAccountApiVersion?.errors?.length) {
      addErrorToast({
        description:
          'We were unable to update your API version, please try again.',
        heading: 'API version update failed',
      });
    } else {
      addSuccessToast({
        description: 'Your API version has been updated.',
        heading: 'API version updated',
      });
    }
  };

  if (loading) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>API Settings</title>
      </Helmet>
      <div className="max-w-3xl space-y-8">
        <APIKeys keyPublic={publishableToken} keySecret={secretToken} />
        <APISettings
          onChange={handleUpdateSettings}
          remoteAllowedReferrers={remoteAllowedReferrers}
          webhookUrl={webhookUrl}
        />
        <APISuppressionSettings
          onChange={handleUpdateSuppressionSettings}
          value={suppressionSettings!}
        />
        <APIVersions
          apiVersions={apiVersions!}
          onUpgrade={handleUpdateAPIVersion}
        />
      </div>
    </>
  );
};
